import { useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { useTheme } from 'styled-components';
import { Unit } from '@/types';
import { renderUnit, renderValue } from '@/data/units';
import { renderVisualisationTooltip } from '@/components/visualisation-tooltip';

// The font stack the app's other charts use (emissions-by-sector-source,
// disclosure-treemap) — set on the chart so labels match them.
// eslint-disable-next-line max-len
const CHART_FONT_FAMILY = `-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Helvetica Neue', Helvetica, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'`;

// A reusable stacked-column chart for disclosure visuals. `categories` are the
// x-axis labels; each `series` is one stack segment, already resolved to its
// display `name` + `color` by the caller (the component reshapes nothing — it
// renders the series straight). Values are in `unit`; a `null` datum is just
// no segment for that category. Mirrors the app's column/legend/tooltip
// conventions (emissions-by-sector-source) as a vertical stacked column.
const DisclosureStackedColumn = (props: DisclosureStackedColumnProps) => {
  const theme = useTheme() as any;
  const { categories, series, unit, height = 360 } = props;

  const options = useMemo(
    () => ({
      chart: {
        type: 'column',
        height,
        animation: false,
        spacing: [8, 1, 8, 1],
        style: { fontFamily: CHART_FONT_FAMILY, fontSize: '12px' },
      },
      title: { text: undefined },
      credits: { enabled: false },
      legend: {
        enabled: true,
        itemStyle: {
          fontWeight: '400',
          fontSize: '12px',
          color: 'rgba(0,0,0,0.65)',
        },
      },
      plotOptions: {
        column: {
          stacking: 'normal',
          borderWidth: 1,
          borderColor: '#ffffff',
          maxPointWidth: 96,
          states: { hover: { enabled: false }, inactive: { opacity: 1 } },
        },
        series: { animation: false },
      },
      xAxis: {
        categories,
        gridLineWidth: 0,
        lineColor: theme.colors.gray5.hex,
        tickColor: theme.colors.gray5.hex,
        tickLength: 6,
        labels: { style: { fontSize: '12px', color: 'rgba(0,0,0,0.65)' } },
      },
      yAxis: {
        gridLineWidth: 1,
        gridLineColor: theme.colors.gray4.hex,
        lineColor: theme.colors.gray5.hex,
        lineWidth: 1,
        title: {
          text: `Emissions (${renderUnit(unit)})`,
          style: {
            color: 'rgba(0,0,0,0.85)',
            fontWeight: 500,
            fontSize: '12px',
          },
        },
        labels: {
          formatter(o: any) {
            return renderValue(o.value, unit);
          },
          style: { fontSize: '12px', color: 'rgba(0,0,0,0.65)' },
        },
      },
      tooltip: {
        shared: true,
        useHTML: true,
        backgroundColor: 'rgba(255,255,255,0)',
        borderWidth: 0,
        shadow: false,
        animation: false,
        hideDelay: 0,
        outside: true,
        padding: 0,
        formatter(): any {
          const o = this as any;
          return renderVisualisationTooltip({
            title: `${o.x}`,
            entries: o.points.map((point: any) => ({
              color: point.series.color,
              label: point.series.name,
              value: point.y,
              unit,
            })),
          });
        },
      },
      series,
    }),
    [theme, categories, series, unit, height],
  );

  // `immutable`: recreate the chart on options changes rather than mutating in
  // place — the same defensive choice the treemap makes against Highcharts 10's
  // incremental-update path, and a no-op cost here since animation is off and
  // `options` is memoised.
  return (
    <HighchartsReact highcharts={Highcharts} options={options} immutable />
  );
};

export type DisclosureStackedColumnSeries = {
  name: string;
  color: string;
  data: (number | null)[];
};

export type DisclosureStackedColumnProps = {
  categories: string[];
  series: DisclosureStackedColumnSeries[];
  unit: Unit;
  height?: number;
};

export { DisclosureStackedColumn };
